'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  LayoutDashboard,
  Building2,
  Inbox,
  Users,
  ScanLine,
  Plus,
  Upload,
  Settings,
  BarChart3,
  Target,
  Package,
  MoreHorizontal,
  LogOut,
  User as UserIcon,
} from 'lucide-react';
import { Avatar } from '@/components/ui/avatar';
import { signOutAction } from '@/lib/actions/auth';

type NavItem = {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string; size?: number; strokeWidth?: number }>;
  count?: number;
};

function getWorkspaceNav(inboxCount: number): NavItem[] {
  return [
    { href: '/', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/dostawcy', label: 'Dostawcy', icon: Building2 },
    { href: '/produkty', label: 'Produkty · SKU', icon: Package },
    {
      href: '/inbox',
      label: 'Inbox',
      icon: Inbox,
      count: inboxCount > 0 ? inboxCount : undefined,
    },
    { href: '/dodaj', label: 'Dodaj dostawcę', icon: Plus },
    { href: '/import', label: 'Import TSV', icon: Upload },
    { href: '/skanuj', label: 'Skanuj wizytówkę', icon: ScanLine },
  ];
}

const insightsNav: NavItem[] = [
  { href: '/akcje', label: 'Akcje', icon: Target },
  { href: '/kontakty', label: 'Kontakty', icon: Users },
  { href: '/raporty', label: 'Raporty', icon: BarChart3 },
  { href: '/ustawienia', label: 'Ustawienia', icon: Settings },
];

export type SidebarUser = {
  name: string | null;
  email: string;
  image: string | null;
};

export function Sidebar({ user, inboxCount = 0 }: { user: SidebarUser | null; inboxCount?: number }) {
  const pathname = usePathname();
  const workspaceNav = getWorkspaceNav(inboxCount);

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <div className="mark">K</div>
        <div className="col" style={{ gap: 0 }}>
          <div className="name">KROX</div>
          <div className="sub">FIX · Procurement</div>
        </div>
      </div>

      <div className="sidebar-section">
        <div className="sidebar-section-label">Workspace</div>
        <nav className="sidebar-nav">
          {workspaceNav.map((item) => (
            <NavLink key={item.href} item={item} active={isActive(pathname, item.href)} />
          ))}
        </nav>
      </div>

      <div className="sidebar-section">
        <div className="sidebar-section-label">Insights</div>
        <nav className="sidebar-nav">
          {insightsNav.map((item) => (
            <NavLink key={item.href} item={item} active={isActive(pathname, item.href)} />
          ))}
        </nav>
      </div>

      <UserFooter user={user} />
    </aside>
  );
}

function NavLink({ item, active }: { item: NavItem; active: boolean }) {
  const Icon = item.icon;
  return (
    <Link
      href={item.href}
      className={`nav-item ${active ? 'active' : ''}`}
    >
      <Icon className="nav-icon" size={16} strokeWidth={1.75} />
      <span>{item.label}</span>
      {item.count != null && item.count > 0 && (
        <span className="count">{item.count}</span>
      )}
    </Link>
  );
}

function isActive(pathname: string, href: string): boolean {
  if (href === '/') return pathname === '/';
  return pathname.startsWith(href);
}

// =============================================================================
// UserFooter — info zalogowanego user + dropdown z sign out
// =============================================================================

function UserFooter({ user }: { user: SidebarUser | null }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Close on Esc
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [menuOpen]);

  // Brak user — anonimowy state (np. development bez auth)
  if (!user) {
    return (
      <div className="sidebar-footer">
        <span className="avatar av-5">?</span>
        <div className="who">
          <div className="n">Anonimowy</div>
          <div className="e">brak sesji</div>
        </div>
      </div>
    );
  }

  const displayName = user.name ?? user.email.split('@')[0];

  return (
    <div className="sidebar-footer" ref={menuRef} style={{ position: 'relative' }}>
      <Avatar name={displayName} imageUrl={user.image} />
      <div className="who">
        <div className="n">{displayName}</div>
        <div className="e">{user.email}</div>
      </div>
      <button
        type="button"
        className="more"
        aria-label="Więcej"
        onClick={() => setMenuOpen((v) => !v)}
      >
        <MoreHorizontal size={16} />
      </button>

      {menuOpen && (
        <div className="user-menu">
          <Link
            href="/ustawienia"
            className="user-menu-item"
            onClick={() => setMenuOpen(false)}
          >
            <UserIcon size={13} />
            Mój profil
          </Link>
          <form
            action={signOutAction}
            style={{ margin: 0 }}
          >
            <button type="submit" className="user-menu-item user-menu-item-danger">
              <LogOut size={13} />
              Wyloguj się
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
